import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Tab,
  Tabs,
  Badge
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeart,
  faStar,
} from '@fortawesome/free-solid-svg-icons';

import Notification, { useNotification } from 'src/Components/Notification';
import Paginator from 'src/Components/Paginator';
import { formatCurrency } from "src/Utils/formatters"
import { createRaverOrder, getSpotMenu, updateRaverOrder } from 'src/Services/api';
import { getFromStore, saveToStore, removeFromStore } from 'src/Utils/storage-helpers';
import useQuery from 'src/Hooks/useQuery';
import { IS_CLOSED } from '../tab/constants';

import './styles.scss';

const TABLE_TAG_STORE_KEY = 'table_tag';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const RaverMenuList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [quantities, setQuantities] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showTabModal, setShowTabModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [tableTag, setTableTag] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [raverEmail, setRaverEmail] = useState('');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { notifySuccess, notifyError, onClose, notification } = useNotification();
  
  // Debounce search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  let { spotUuid: spotId } = useParams();
  
  const params = useMemo(() => ({ 
    spot: spotId, 
    page: currentPage 
  }), [spotId, currentPage]);

  const { loading, response = {} } = useQuery(getSpotMenu, params);
  const { data = {} } = response;
  const { objects: menuList = [], total_pages } = data;

  // Filter menu items based on active category
  const filteredMenuList = useMemo(() => {
    if (activeCategory === 'all') {
      return menuList;
    }
    return menuList.filter(item => item.category === activeCategory);
  }, [menuList, activeCategory]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(
      menuList
        .map(item => item.category)
        .filter(category => category !== null && category !== undefined)
    )];
    return uniqueCategories.sort((a, b) => a - b);
  }, [menuList]);

  let spotName = "";
  if (menuList.length > 0) {
    spotName = menuList[0].spot_name;
  }

  // Reset page when category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, debouncedSearchTerm]);

  useEffect(() => {
    const urlTag = searchParams.get('table');
    if (urlTag) {
      setTableTag(urlTag);
      saveToStore(TABLE_TAG_STORE_KEY, urlTag);
    } else {
      getFromStore(TABLE_TAG_STORE_KEY).then((stored) => {
        if (stored) setTableTag(stored);
      });
    }
  }, [searchParams]);

  useEffect(() => {
    const checkTabValidity = async () => {
      const modalShownKey = `tabModalShown_${spotId}`;
      const wasModalShown = await getFromStore(modalShownKey);

      if (wasModalShown) return;

      const tab = await getFromStore('tab');
      const isTabClosed = tab?.state === IS_CLOSED;
      const isTabExpired = tab?.created_at && (new Date() - new Date(tab.created_at) > 24 * 60 * 60 * 1000);
      const isDifferentSpot = spotId !== tab?.spot?.id?.toString();

      if (tab && (isTabClosed || isTabExpired || isDifferentSpot)) {
        await removeFromStore('tab');
      } else if (tab) {
        setShowTabModal(true);
        await saveToStore(modalShownKey, 'true');
      }
    };

    checkTabValidity();
  }, [spotId]);

  const handleReuseTab = async(res) => {
    setIsLoading(true);
    const tab = await getFromStore('tab');
    if (res === "yes") {
      if (tab && tab.state === IS_CLOSED) {
        const openTabRes = await updateRaverOrder(
          { slug: tab.slug },
          { action: 'is_opened' }
        );
        if (openTabRes.status !== 200) {
          notifyError({ message: "An error occured while opening a new tab." });
        } else {
          saveToStore('tab', openTabRes.data);
        }
      }
    } else {
      await removeFromStore('tab');
    }
    setShowTabModal(false);
    setIsLoading(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleQuantityChange = (itemId, value) => {
    if (value === '') {
      setQuantities((prev) => ({
        ...prev,
        [itemId]: '',
      }));
    } else {
      setQuantities((prev) => ({
        ...prev,
        [itemId]: Math.max(1, Number(value)),
      }));
    }
  };  

  const handleAddToTab = (itemId) => {
    const quantity = Number(quantities[itemId]) || 1;
    handleOrder(itemId, quantity);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  const handleViewTab = () => {
    const tabPath = `/raver/${spotId}/tab`;
    navigate(
      tableTag ? `${tabPath}?table=${encodeURIComponent(tableTag)}` : tabPath,
      { state: { spotId } }
    );
  };

  // const handleSearchChange = (e) => {
  //   setSearchTerm(e.target.value);
  // };

  // const clearSearch = () => {
  //   setSearchTerm('');
  // };

  const updateExistingOrder = async (order, qty) => {
    const updatedOrderRes = await updateRaverOrder(
      { id: order.id },
      { quantity: order.quantity + qty }
    );
    if (updatedOrderRes.status !== 200) {
      notifyError({ message: updatedOrderRes.response.data.detail });
    } else {
      const { data: { tab } } = updatedOrderRes;
      saveToStore('tab', tab);
    }
  };

  const addNewOrderToExistingTab = async (menuId, qty, tab) => {
    const newOrderRes = await createRaverOrder({
      menu_id: menuId,
      quantity: qty,
      tab_id: tab.id
    });
    if (newOrderRes.status !== 200) {
      console.error(newOrderRes);
      notifyError({ message: "An error occured while adding the new order." });
    } else {
      const { data: { tab } } = newOrderRes;
      saveToStore('tab', tab);
    }
  };

  const createNewTabWithOrder = async (menuId, qty, spotId, email) => {
    const response = await createRaverOrder({
      menu_id: menuId,
      quantity: qty,
      spot_id: spotId,
      email: email || undefined,
      table_tag: tableTag || undefined
    });
    if (response.status !== 200) {
      console.error(response);
      notifyError({ message: "An error occured while trying to create your tab." });
    } else {
      const { data: { tab } } = response;
      saveToStore('tab', tab);
    }
  };

  const fetchOrCreateTab = async (menuId, qty, email) => {
    setIsLoading(true);
    const tab = await getFromStore('tab');
    if (!menuId) return;
  
    if (tab) {
      const order = tab.orders?.find(order => order.menu_item.id === menuId);
      if (order && order.deleted_at === null) {
        await updateExistingOrder(order, qty);
      } else {
        await addNewOrderToExistingTab(menuId, qty, tab);
      }
    } else {
      await createNewTabWithOrder(menuId, qty, spotId, email);
    }
    setIsLoading(false);
  };

  const handleOrder = async (menuId, qty) => {
    const tab = await getFromStore('tab');
    if (!tab) {
      setPendingOrder({ menuId, qty });
      return;
    }
    await fetchOrCreateTab(menuId, qty);
    notifySuccess({ message: "Item added to your tab successfully." });
  };

  const isEmailValid = !raverEmail.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raverEmail.trim());

  const handleConfirmEmail = async () => {
    const { menuId, qty } = pendingOrder;
    setPendingOrder(null);
    await fetchOrCreateTab(menuId, qty, raverEmail.trim());
    setRaverEmail('');
    notifySuccess({ message: "Item added to your tab successfully." });
  };

  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') {
      return menuList.length;
    }
    return menuList.filter(item => item.category === categoryId).length;
  };

  return (
    <Container fluid className="p-4 menu-container">
      <Notification id="tab-order-update" onClose={onClose} {...notification} />

      <Modal show={!!pendingOrder} onHide={() => setPendingOrder(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Your email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            We use your email to identify your tab and send your receipt. Leave it
            blank to continue without a receipt.
          </p>
          <Form.Control
            type="email"
            placeholder="you@example.com"
            value={raverEmail}
            isInvalid={!isEmailValid}
            onChange={(e) => setRaverEmail(e.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            Enter a valid email address.
          </Form.Control.Feedback>
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ backgroundColor: '#41889A', border: 'none' }}
            disabled={!isEmailValid}
            onClick={handleConfirmEmail}
          >
            {raverEmail.trim() ? 'Continue' : 'Skip'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div className="d-flex align-items-center gap-2">
          <h3 className='menu-title mb-0'>{spotName}</h3>
          {tableTag && (
            <Badge bg="light" text="dark" className="menu-table-tag-badge">
              {tableTag}
            </Badge>
          )}
        </div>
        <Button
          style={{ backgroundColor: '#41889A' }}
          className="text-white border-0"
          onClick={handleViewTab}
          // hidden={true}
        >
          View Tab
        </Button>
      </div>

      {/* <Row className='mb-4 align-items-center'>
        <Col md={8}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search menu items..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <InputGroup.Text className="search-icon-container">
              {searchTerm ? (
                <FontAwesomeIcon 
                  icon={faTimes} 
                  className="search-clear-icon"
                  onClick={clearSearch}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
              )}
            </InputGroup.Text>
          </InputGroup>
        </Col>
      </Row> */}

      <div className="category-tabs-container mb-4">
        <div className="scrollable-tabs-wrapper">
          <Tabs
            activeKey={activeCategory}
            onSelect={handleCategoryChange}
            className="menu-category-tabs scrollable-tabs"
            variant="pills"
          >
            <Tab 
              eventKey="all" 
              title={
                <span>
                  All
                  <Badge bg="secondary" className="ms-2">
                    {menuList.length}
                  </Badge>
                </span>
              }
            />
            {categories.map((categoryId) => (
              <Tab
                key={categoryId}
                eventKey={categoryId.toString()}
                title={
                  <span>
                    {categoryId}
                    <Badge bg="secondary" className="ms-2">
                      {getCategoryCount(categoryId)}
                    </Badge>
                  </span>
                }
              />
            ))}
          </Tabs>
        </div>
      </div>

      {/* Menu Items */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner
            as="span"
            animation="border"
            size="lg"
            role="status"
            aria-hidden="true"
            style={{ color: '#41889A' }}
          />
          <div className="mt-2">Loading menu...</div>
        </div>
      ) : (
        filteredMenuList.length === 0 ? (
          <div className="text-center py-5">
            <h5 className="text-muted">No items found</h5>
            <p className="text-muted">
              {activeCategory !== 'all' 
                ? `No items available in ${activeCategory} category`
                : 'This spot has no menu items available'
              }
            </p>
          </div>
        ) : (
          <>
            <Row>
              {filteredMenuList.map((item, index) => (
                <Col key={index} xs={12} sm={12} md={6} lg={4} className='mb-4'>
                  <Card className='menu-card shadow-sm border-0 h-100'>
                    <div className="position-relative">
                      <Card.Img variant='top' src={item.image} className='menu-item-img' />
                      {item.category && (
                        <Badge
                          bg="dark"
                          className="position-absolute top-0 start-0 m-2 category-badge"
                        >
                          {item.category}
                        </Badge>
                      )}
                    </div>
                    <Card.Body className='d-flex flex-column'>
                      <div className='d-flex d-none justify-content-between align-items-center mb-2 text-secondary'>
                        <span className='like-icon'>
                          <FontAwesomeIcon icon={faHeart} />
                        </span>
                        <span className='rating-icon'>
                          <FontAwesomeIcon icon={faStar} /> 4.5
                        </span>
                      </div>
                      <Card.Title className='menu-item-title d-flex justify-content-between align-items-start'>
                        <h5 className="mb-1">{item.name}</h5>
                        <h5 className='menu-item-price'>{formatCurrency(item.price, item.currency)}</h5>
                      </Card.Title>
                      <Card.Text className='menu-item-description text-muted flex-grow-1'>
                        {item.description}
                      </Card.Text>
                      <div className='mt-auto d-flex justify-content-between align-items-center pt-3'>
                        {item.is_available ? (
                          <>
                            <Form.Control
                              type='number'
                              min={1}
                              step={1}
                              placeholder='1'
                              value={quantities[item.id] ?? ''}
                              onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                              className='me-2'
                              style={{ width: '70px', textAlign: 'center' }}
                              // hidden={true}
                            />
                            <Button
                              variant='outline-custom'
                              className='add-to-tab-btn flex-grow-1'
                              onClick={() => handleAddToTab(item.id)}
                              disabled={isLoading}
                              // hidden={true}
                            >
                              {isLoading ? (
                                <Spinner size="sm" animation="border" />
                              ) : (
                                'Add To Tab'
                              )}
                            </Button>
                          </>
                        ) : (
                          <div className="d-flex w-100 justify-content-center">
                            <Badge bg="danger" className="unavailable-badge">
                              Unavailable
                            </Badge>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <small className="text-muted">
                Showing {filteredMenuList.length} items
                {activeCategory !== 'all' && ` in ${activeCategory}`}
              </small>
            </div>

            {total_pages > 1 && (
              <div className="d-flex justify-content-center">
                <Paginator
                  totalPages={total_pages}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )
      )}

      {/* Tab Modal */}
      <Modal
        show={showTabModal}
        onHide={() => setShowTabModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Existing Tab Found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <p>You have an existing Tab running for this spot.</p>
          </Container>
        </Modal.Body>
        <Modal.Footer className="modalFooter d-flex flex-column flex-sm-row">
          <Button
            className="jamlyfSolidBtn w-100 flex-sm-fill"
            onClick={() => handleReuseTab("no")}
            disabled={isLoading}
          >
            Start new tab
          </Button>
          <Button
            className="dangerOutlineBtn w-100 flex-sm-fill"
            onClick={() => handleReuseTab("yes")}
            disabled={isLoading}
          >
            Continue with tab
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RaverMenuList;
